import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthCard from '../../components/auth/AuthCard';
import BrandsLogin from '../../components/auth/BrandsLogin';
import CheckEmail from '../../components/auth/CheckEmail';
import { base64Decode } from '../../helpers/decodeToken';
import { AuthService } from '../../service/AuthService';
import { LocalStorageService } from '../../service/LocalStorageService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import { setPendingConfirmSameBrowser } from './ConfirmAccount';

type AuthMode = 'email' | 'login' | 'register';

type IFormInput = {
  email: string;
  password: string;
  confirmPassword?: string;
  name?: string;
  termsAndConditions?: boolean;
};

const variants = {
  initial: { opacity: 0, x: 10 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -10 },
};

const Login = () => {
  const navigate = useNavigate();
  const { setNotification } = useNotificationStore(state => state);
  const [authMode, setAuthMode] = useState<AuthMode>('email');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [showResendEmailVerification, setShowResendEmailVerification] = useState(false);
  const [registrationCompleted, setRegistrationCompleted] = useState(false);
  const [emailToResend, setEmailToResend] = useState('');
  const [tokenData, setTokenData] = useState<{ token: string; decoded: { email: string } }>({
    token: '',
    decoded: { email: '' },
  });

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<IFormInput>();

  const { setAuthUser, setAccessToken } = useAuthStore(state => state);
  const location = useLocation();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setTokenData({ token, decoded: base64Decode(token) });
      setValue('email', base64Decode(token).email);
    }
  }, []);

  const onCheckEmail = async (values: IFormInput) => {
    if (!values.email) {
      setError('email', { type: 'validate', message: 'Email is required' });
      return;
    }
    setIsCheckingEmail(true);
    try {
      const response = await AuthService.checkEmail(values.email);
      setAuthMode(response?.data?.exists ? 'login' : 'register');
    } catch (error: any) {
      setNotification({ type: 'error', message: error.message || 'Failed to check email' });
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleSwitchToRegister = () => {
    clearErrors();
    setAuthMode('register');
    setValue('password', '');
    setValue('confirmPassword', '');
    setShowResendEmailVerification(false);
  };

  const handleSwitchToLogin = () => {
    clearErrors();
    setAuthMode('login');
    setValue('password', '');
    setValue('confirmPassword', '');
    setValue('name', '');
    setShowResendEmailVerification(false);
  };

  const handleSwitchToEmail = () => {
    clearErrors();
    setAuthMode('email');
    setValue('password', '');
    setValue('confirmPassword', '');
    setValue('name', '');
    setShowResendEmailVerification(false);
  };

  const resendEmailVerification = async (values: IFormInput) => {
    try {
      const response = await AuthService.resendVerificationEmail({ email: values.email });
      setNotification({ type: 'success', message: response?.data?.message ?? 'Email sent' });
    } catch (error: any) {
      setNotification({ type: 'error', message: error.message });
    }
  };

  const resendRegistrationVerificationEmail = async () => {
    try {
      const response = await AuthService.resendVerificationEmail({ email: emailToResend });
      setNotification({ type: 'success', message: response?.data?.message ?? 'Email sent' });
    } catch (error: any) {
      setNotification({ type: 'error', message: error.message });
    }
  };

  const onSubmitLogin = async (values: IFormInput) => {
    try {
      const response = await AuthService.login(values.email, values.password);
      setAuthUser(response.data);
      LocalStorageService.setItem('accessToken', response.data.accessToken);
      setAccessToken(response.data.accessToken);
      const urlParams = new URLSearchParams(location.search).get('token');
      if (urlParams) {
        setTimeout(() => navigate('/company/accept-invite?token=' + urlParams), 1000);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      if (error.message === 'Password is incorrect!') {
        setError('password', { type: 'validate', message: error.message });
        setNotification({ type: 'error', message: error.message });
      } else if (error.message === 'Email does not exist!') {
        setError('email', { type: 'validate', message: 'Wrong username or password.' });
        setNotification({ type: 'error', message: 'Wrong username or password.' });
      } else if (error.message === 'Please confirm your account!') {
        setShowResendEmailVerification(true);
        setNotification({ type: 'error', message: error.message });
      } else {
        setNotification({ type: 'error', message: error.message || 'Something went wrong' });
      }
    }
  };

  const onSubmitRegister = async (values: IFormInput) => {
    if (!watch('termsAndConditions')) {
      setError('termsAndConditions', { type: 'validate', message: 'You must agree to the terms and conditions' });
      return;
    }
    try {
      const userData = await AuthService.register(values, tokenData.token);
      if (tokenData.token) {
        setAuthUser(userData.data);
        LocalStorageService.setItem('accessToken', userData.data.accessToken);
        setAccessToken(userData.data.accessToken);
        navigate('/');
      } else {
        setEmailToResend(values.email);
        setRegistrationCompleted(true);
        setPendingConfirmSameBrowser();
        setNotification({ type: 'success', message: 'User registered successfully' });
      }
    } catch (error: any) {
      if (error.message === 'User already exist!') {
        setError('email', { type: 'validate', message: 'Email already exists' });
        setNotification({ type: 'error', message: 'Email already exists' });
      } else {
        setNotification({ type: 'error', message: error.message || 'Something went wrong' });
      }
    }
  };

  const onSubmit = (values: IFormInput) => {
    if (authMode === 'email') onCheckEmail(values);
    else if (authMode === 'login') onSubmitLogin(values);
    else onSubmitRegister(values);
  };

  if (registrationCompleted) {
    return (
      <AuthCard>
        <CheckEmail
          bodyText="We have sent you an email with a link to verify your account. Please check your email and click on the link to verify your account."
          onNavigate={() => { setRegistrationCompleted(false); setAuthMode('email'); setValue('email', ''); }}
          resendEmailVerification={resendRegistrationVerificationEmail}
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard>
      <AnimatePresence mode="wait">
        <motion.div
          key={authMode}
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants}
          transition={{ duration: 0.2 }}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
            <div className="auth-form__header">
              <div>
                <h2 className="auth-form__title">
                  {authMode === 'email' && <>Welcome <strong>back</strong></>}
                  {authMode === 'login' && <>Sign <strong>in</strong></>}
                  {authMode === 'register' && <>Create <strong>Account</strong></>}
                </h2>
                {authMode === 'email' && (
                  <p className="auth-form__subtitle">Enter your email to continue</p>
                )}
              </div>
              {authMode === 'login' && (
                <button type="button" onClick={handleSwitchToRegister} className="auth-btn-link auth-btn-link--md">
                  Create Account
                </button>
              )}
              {authMode === 'register' && (
                <button type="button" onClick={handleSwitchToLogin} className="auth-btn-link auth-btn-link--md">
                  Sign In Instead
                </button>
              )}
            </div>

            <div className="auth-form__fields">
              {authMode === 'register' && (
                <div className="auth-form__field">
                  <label className="auth-label">Name</label>
                  <input
                    type="text"
                    {...register('name', { required: 'Name is required' })}
                    className="auth-input"
                  />
                  {errors.name && <p className="auth-error">{errors.name.message}</p>}
                </div>
              )}

              <div className="auth-form__field">
                <div className="auth-form__field-row">
                  <label className="auth-label">Email</label>
                  {authMode !== 'email' && (
                    <button type="button" onClick={handleSwitchToEmail} className="auth-btn-link auth-btn-link--sm">
                      Change
                    </button>
                  )}
                </div>
                <input
                  type="email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Invalid email address' },
                  })}
                  readOnly={authMode !== 'email'}
                  className={`auth-input${authMode !== 'email' ? ' auth-input--readonly' : ''}`}
                />
                {errors.email && <p className="auth-error">{errors.email.message}</p>}
              </div>

              {(authMode === 'login' || authMode === 'register') && (
                <div className="auth-form__field">
                  <div className="auth-form__field-row">
                    <label className="auth-label">Password</label>
                    {authMode === 'login' && (
                      <button type="button" onClick={() => navigate('/forgot-password')} className="auth-btn-link auth-btn-link--sm">
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <input
                    type="password"
                    {...register('password', {
                      required: 'Password is required',
                      ...(authMode === 'register' && {
                        minLength: { value: 8, message: 'Password must be at least 8 characters' },
                        pattern: {
                          value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{6,}$/,
                          message: 'Must contain uppercase, lowercase, number and special character (@$!%*?&#)',
                        },
                      }),
                    })}
                    className="auth-input"
                  />
                  {errors.password && <p className="auth-error">{errors.password.message}</p>}
                </div>
              )}

              {authMode === 'register' && (
                <div className="auth-form__field">
                  <label className="auth-label">Confirm Password</label>
                  <input
                    type="password"
                    {...register('confirmPassword', {
                      required: 'Confirm Password is required',
                      validate: value => value === watch('password') || 'The passwords do not match',
                    })}
                    className="auth-input"
                  />
                  {errors.confirmPassword && <p className="auth-error">{errors.confirmPassword.message}</p>}
                </div>
              )}

              {authMode === 'login' && (
                <div className="auth-checkbox">
                  <input type="checkbox" id="remember" />
                  <label htmlFor="remember" className="auth-checkbox__label">Remember me</label>
                </div>
              )}

              {authMode === 'login' && showResendEmailVerification && (
                <button
                  type="button"
                  onClick={handleSubmit(resendEmailVerification)}
                  className="auth-btn-link auth-btn-link--md auth-btn-link--grey"
                >
                  Resend verification email
                </button>
              )}

              {authMode === 'register' && (
                <div className="auth-checkbox auth-checkbox--top">
                  <input
                    type="checkbox"
                    id="terms"
                    {...register('termsAndConditions', { required: 'You must agree to the terms and conditions' })}
                  />
                  <label
                    htmlFor="terms"
                    className={`auth-checkbox__label${errors.termsAndConditions ? ' auth-checkbox__label--error' : ''}`}
                  >
                    I agree to the Terms and Conditions
                  </label>
                </div>
              )}
              {errors.termsAndConditions && (
                <p className="auth-error">{errors.termsAndConditions.message}</p>
              )}
            </div>

            <button type="submit" disabled={isCheckingEmail} className="auth-btn-primary">
              {isCheckingEmail
                ? 'Checking...'
                : authMode === 'email'
                ? 'Continue'
                : authMode === 'login'
                ? 'Sign In'
                : 'Create Account'}
            </button>
          </form>

          <BrandsLogin
            text={authMode === 'email' ? 'Or continue with' : authMode === 'login' ? 'Or sign in with' : 'Or register with'}
            type={authMode === 'register' ? 'register' : 'login'}
          />
        </motion.div>
      </AnimatePresence>
    </AuthCard>
  );
};

export default Login;
